import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { 
  FaUser, 
  FaPhone, 
  FaMapMarkerAlt, 
  FaUserFriends,
  FaCamera,
  FaTimes,
  FaBriefcase,
  FaHeart
} from "react-icons/fa";
import {
  TextInputComp,
  ButtonComp,
  FormikSelectComp,
  FormikDatePicker,
  CardComp,
} from "../../../components";
import { PageTitleWithNav } from "../../Admin/modules/PageHeaders";
import { toast } from "react-hot-toast";
import uploadImage from "../../../utils/uploadImage";
import { useUpdateMemberProfileMutation, useGetCurrentMemberProfileQuery } from "../../../store/auth/authApi";
import {
  useGetTitlesQuery,
  useGetMaritalStatusesQuery,
  useGetGendersQuery,
  useGetRelationshipStatusesQuery,
} from "../../../store/selectableSlice";
import Loader from "../../../components/Ui/Loader";

const EditMemberProfile = () => {
  const navigate = useNavigate();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateMemberProfileMutation();
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [errorImg, setErrorImg] = useState(null);
  const fileInputRef = useRef(null);

  const { data: memberDetails = {}, isLoading: isLoadingProfile, error } = useGetCurrentMemberProfileQuery();
  const { data: titles = [] } = useGetTitlesQuery();
  const { data: genders = [] } = useGetGendersQuery();
  const { data: maritalStatuses = [] } = useGetMaritalStatusesQuery();
  const { data: relationshipStatuses = [] } = useGetRelationshipStatusesQuery();

  const validationSchema = Yup.object({
    first_name: Yup.string().trim().required("First name is required"),
    last_name: Yup.string().trim().required("Last name is required"),
    phone: Yup.string().trim().matches(/^[0-9]{11}$/, "Phone number must be 11 digits").required("Phone number is required"),
    address: Yup.string().trim().required("Address is required"),
    title_id: Yup.number().required("Title is required"),
    marital_id: Yup.number().required("Marital status is required"),
    gender_id: Yup.number().required("Gender is required"),
    dob: Yup.string().required("Date of birth is required"),
    occupation: Yup.string().trim().required("Occupation is required"),
    relationship_status_id: Yup.number().required("Relationship is required"),
    next_of_kin_name: Yup.string().trim().required("Next of kin name is required"),
    next_of_kin_phone: Yup.string().trim().required("Next of kin phone is required"),
  });

  const formik = useFormik({
    initialValues: {
      first_name: memberDetails?.first_name || "",
      last_name: memberDetails?.last_name || "",
      other_name: memberDetails?.other_name || "",
      phone: memberDetails?.phone || "",
      address: memberDetails?.address || "",
      title_id: memberDetails?.title_id || "",
      marital_id: memberDetails?.marital_id || "",
      gender_id: memberDetails?.gender_id || "",
      dob: memberDetails?.dob || "",
      occupation: memberDetails?.occupation || "",
      relationship_status_id: memberDetails?.relationship_status_id || "",
      next_of_kin_name: memberDetails?.next_of_kin_name || "",
      next_of_kin_phone: memberDetails?.next_of_kin_phone || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      console.log("Submitting values:", values);
      try {
        let photoUrl = memberDetails?.photo;
        if (imgFile) {
          const toastId = toast.loading("Uploading image...");
          try {
            const uploadedUrl = await uploadImage(imgFile);
            if (uploadedUrl) {
              photoUrl = uploadedUrl;
              toast.success("Image uploaded", { id: toastId });
            } else {
              toast.error("Image upload failed", { id: toastId });
              return;
            }
          } catch (uploadError) {
            toast.error("Error uploading image", { id: toastId });
            return;
          }
        }

        const payload = { ...values, photo: photoUrl };
        console.log("Final payload:", payload);
        const res = await updateProfile(payload).unwrap();
        
        if (res) {
          toast.success("Profile updated successfully!");
          navigate("/member/profile");
        }
      } catch (err) {
        console.error("Update error:", err);
        toast.error(err?.data?.message || "Failed to update profile");
      }
    },
  });

  const handleImageChange = (file) => {
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setErrorImg("File size too large (max 10MB)");
        return;
      }
      setErrorImg(null);
      setImgFile(file);
      setImgPreview(URL.createObjectURL(file));
    }
  };

  if (isLoadingProfile) return <div className="d-flex justify-content-center py-5"><Loader /></div>;

  return (
    <section className="mx-2 mx-lg-4 min-vh-100 pb-5">
      <style>{`
        .profile-container {
          max-width: 900px;
          margin: 0 auto;
        }
        .section-card {
          border-radius: 15px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
          border: none;
          margin-bottom: 25px;
          background: #fff;
          padding: 30px;
        }
        .section-header {
          display: flex;
          align-items: center;
          margin-bottom: 25px;
          border-bottom: 1px solid #f0f0f0;
          padding-bottom: 15px;
        }
        .icon-box {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
          background: var(--color-primary-soft);
          color: var(--color-primary);
        }
        .avatar-wrapper {
          position: relative;
          width: 140px;
          height: 140px;
          margin: 0 auto 30px;
        }
        .avatar-img {
          width: 140px;
          height: 140px;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid #fff;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }
        .camera-btn {
          position: absolute;
          bottom: 5px;
          right: 5px;
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: var(--color-primary);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border: 3px solid #fff;
          transition: all 0.3s ease;
        }
        .camera-btn:hover {
          transform: scale(1.1);
          background: color-mix(in srgb, var(--color-primary) 80%, black);
        }
        .form-label {
          font-weight: 600;
          color: #383838;
          font-size: 14px;
          margin-bottom: 8px;
        }
        .custom-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 20px;
        }
        .full-width {
          grid-column: 1 / -1;
        }
        .form-control, .form-select {
          height: 45px !important;
          background-color: #FAFAFA !important;
          border: 1px solid #E0E0E0 !important;
          font-size: 14px !important;
        }
        .form-control:focus, .form-select:focus {
          border-color: var(--color-primary) !important;
          box-shadow: none !important;
        }
        .action-button {
          height: 52px !important;
          border-radius: 12px !important;
          font-weight: 600 !important;
          font-size: 16px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 0 30px !important;
        }
        .btn-cancel {
          border: 2px solid var(--color-primary) !important;
          color: var(--color-primary) !important;
          background: transparent !important;
        }
        .btn-cancel:hover {
          background: var(--color-primary-soft) !important;
          transform: translateY(-2px);
        }
        .btn-save {
          background: var(--color-primary) !important;
          color: #fff !important;
          border: none !important;
          box-shadow: 0 4px 15px var(--color-border) !important;
        }
        .btn-save:hover:not(:disabled) {
          background: color-mix(in srgb, var(--color-primary) 80%, black) !important;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(139, 49, 58, 0.3) !important;
        }
        .btn-save:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .error-text {
          color: #d40000;
          font-size: 12px;
          margin-top: 5px;
          display: block;
        }
      `}</style>

      <div className="py-2"></div>
      <PageTitleWithNav title="Edit Profile" action={() => navigate(-1)} />

      <div className="profile-container">
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            
            {/* Profile Header Card */}
            <div className="section-card text-center">
              <div className="avatar-wrapper">
                <img 
                  src={imgPreview || memberDetails?.photo || "/Staff/avatar.svg"} 
                  className="avatar-img" 
                  alt="Profile" 
                />
                <div className="camera-btn" onClick={() => fileInputRef.current.click()}>
                  <FaCamera size={16} />
                </div>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  hidden 
                  accept="image/*" 
                  onChange={(e) => handleImageChange(e.target.files[0])} 
                />
              </div>
              <h4 className="fw-bold mb-1">{formik.values.first_name} {formik.values.last_name}</h4>
              <p className="text-muted small mb-0">
                Membership Number: {memberDetails?.member_no || "---"}
              </p>
              {errorImg && <small className="text-danger d-block mt-2">{errorImg}</small>}
            </div>

            {/* Personal Information */}
            <div className="section-card">
              <div className="section-header">
                <div className="icon-box"><FaUser /></div>
                <div>
                  <h5 className="fw-bold mb-0">Personal Information</h5>
                  <small className="text-muted">Basic details about you</small>
                </div>
              </div>
              
              <div className="custom-grid">
                <div>
                  <FormikSelectComp 
                    fieldName="title_id" 
                    labelText="Title" 
                    isRequired 
                    options={titles.map(t => ({ value: t.id, label: t.name }))} 
                  />
                </div>
                <div>
                  <TextInputComp 
                    labelText="First Name" 
                    name="first_name" 
                    required 
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                <div>
                  <TextInputComp 
                    labelText="Middle Name" 
                    name="other_name"
                    value={formik.values.other_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                <div>
                  <TextInputComp 
                    labelText="Last Name" 
                    name="last_name" 
                    required 
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                <div>
                  <FormikSelectComp 
                    fieldName="gender_id" 
                    labelText="Gender" 
                    isRequired 
                    options={genders.map(g => ({ value: g.id, label: g.name }))} 
                  />
                </div>
                <div>
                  <FormikDatePicker fieldName="dob" labelText="Date of Birth" isRequired />
                </div>
                <div>
                  <FormikSelectComp 
                    fieldName="marital_id" 
                    labelText="Marital Status" 
                    isRequired 
                    options={maritalStatuses.map(m => ({ value: m.id, label: m.name }))} 
                  />
                </div>
                <div>
                  <TextInputComp 
                    labelText="Occupation" 
                    name="occupation" 
                    required 
                    value={formik.values.occupation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="section-card">
              <div className="section-header">
                <div className="icon-box" style={{ background: "var(--color-secondary-soft)", color: "var(--color-secondary)" }}>
                  <FaPhone />
                </div>
                <div>
                  <h5 className="fw-bold mb-0">Contact Details</h5>
                  <small className="text-muted">How we can reach you</small>
                </div>
              </div>
              
              <div className="custom-grid">
                <div>
                  <TextInputComp 
                    labelText="Phone Number" 
                    name="phone" 
                    required 
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                <div className="full-width">
                  <TextInputComp 
                    labelText="Residential Address" 
                    name="address" 
                    required 
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
              </div>
            </div>

            {/* Next of Kin */}
            <div className="section-card">
              <div className="section-header">
                <div className="icon-box" style={{ background: "rgba(133, 67, 241, 0.1)", color: "#8543f1" }}>
                  <FaUserFriends />
                </div>
                <div>
                  <h5 className="fw-bold mb-0">Next of Kin</h5>
                  <small className="text-muted">Emergency contact information</small>
                </div>
              </div>
              
              <div className="custom-grid">
                <div className="full-width">
                  <TextInputComp 
                    labelText="Full Name" 
                    name="next_of_kin_name" 
                    required 
                    value={formik.values.next_of_kin_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                <div>
                  <TextInputComp 
                    labelText="Phone Number" 
                    name="next_of_kin_phone" 
                    required 
                    value={formik.values.next_of_kin_phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                <div>
                  <FormikSelectComp 
                    fieldName="relationship_status_id" 
                    labelText="Relationship" 
                    isRequired 
                    options={relationshipStatuses.map(r => ({ value: r.id, label: r.name }))} 
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end align-items-center gap-3 mt-5 pb-5">
              <ButtonComp 
                btnText="Cancel" 
                btnClassName="action-button btn-cancel" 
                style={{ minWidth: "200px" }}
                onClick={() => navigate(-1)} 
                type="button" 
              />
              <ButtonComp 
                btnText="Save Changes" 
                btnClassName="action-button btn-save" 
                style={{ minWidth: "200px" }}
                type="submit" 
                loading={isUpdating} 
              />
            </div>

          </form>
        </FormikProvider>
      </div>
    </section>
  );
};

export default EditMemberProfile;
